/**
 * Odpowiedzialność porządkowa pracowników — art. 108–113 Kodeksu pracy
 * (Etap 38, ADR-0039).
 *
 * Etap 33 dał potrącenie rodzaju „kara pieniężna", ale kary nie miał skąd
 * wziąć: kadry wpisywały kwotę jak każdy inny tytuł. Tymczasem kara porządkowa
 * to POSTĘPOWANIE z własnymi terminami, katalogiem naruszeń i granicami,
 * a potrącenie jest dopiero jego skutkiem.
 *
 * ## Rodzaje kar (art. 108 § 1 i § 2)
 *
 *  - **upomnienie** i **nagana** — za nieprzestrzeganie organizacji i porządku
 *    pracy, przepisów BHP i przeciwpożarowych oraz przyjętego sposobu
 *    potwierdzania obecności i usprawiedliwiania nieobecności,
 *  - **kara pieniężna** — TYLKO za cztery naruszenia z § 2: nieprzestrzeganie
 *    przepisów BHP albo przeciwpożarowych, opuszczenie pracy bez
 *    usprawiedliwienia, stawienie się do pracy w stanie nietrzeźwości (albo po
 *    użyciu środka działającego podobnie) i spożywanie alkoholu w czasie pracy.
 *
 * ## Granice kary pieniężnej (art. 108 § 3)
 *
 *  - za jedno przekroczenie i za każdy dzień nieusprawiedliwionej nieobecności
 *    — nie więcej niż **jednodniowe wynagrodzenie**,
 *  - łącznie w miesiącu — nie więcej niż **1/10 wynagrodzenia** przypadającego
 *    do wypłaty PO potrąceniach z art. 87 § 1 pkt 1–3 (alimenty, inne
 *    należności egzekwowane, zaliczki pieniężne).
 *
 * ## Terminy (art. 109)
 *
 * Kary nie wolno zastosować po **dwóch tygodniach** od powzięcia wiadomości
 * o naruszeniu ani po **trzech miesiącach** od jego popełnienia, a przed
 * ukaraniem trzeba pracownika **wysłuchać**.
 *
 * ## Sprzeciw i zatarcie (art. 112 i art. 113)
 *
 * Pracownik ma **7 dni** na sprzeciw; nieodrzucenie go w ciągu **14 dni** jest
 * równoznaczne z uwzględnieniem. Uwzględnienie sprzeciwu albo orzeczenie sądu
 * każe zwrócić równowartość kary pieniężnej. Po **roku** nienagannej pracy karę
 * uważa się za niebyłą, a odpis zawiadomienia usuwa się z akt osobowych.
 */

/** Rodzaje kar porządkowych z art. 108 § 1 i § 2 k.p. */
export const PENALTY_KINDS = ['upomnienie', 'nagana', 'kara_pieniezna'] as const
export type PenaltyKind = (typeof PENALTY_KINDS)[number]

/**
 * Katalog naruszeń. Cztery pierwsze pozwalają na karę pieniężną (art. 108 § 2);
 * `porzadek` to naruszenia z § 1, przy których zostają upomnienie i nagana.
 */
export const PENALTY_OFFENCES = ['bhp', 'przeciwpozarowe', 'nieobecnosc', 'alkohol', 'porzadek'] as const
export type PenaltyOffence = (typeof PENALTY_OFFENCES)[number]

/** Czy za to naruszenie wolno wymierzyć karę PIENIĘŻNĄ (art. 108 § 2 k.p.). */
export function allowsFine(offence: PenaltyOffence): boolean {
  return offence !== 'porzadek'
}

/** Termin z art. 109 § 1: dwa tygodnie od powzięcia wiadomości o naruszeniu. */
export const PENALTY_NOTICE_DAYS = 14

/** Termin z art. 109 § 1: trzy miesiące od dopuszczenia się naruszenia. */
export const PENALTY_OFFENCE_MONTHS = 3

/** Termin z art. 112 § 1: siedem dni na sprzeciw od zawiadomienia o karze. */
export const PENALTY_OBJECTION_DAYS = 7

/**
 * Termin z art. 112 § 1: nieodrzucenie sprzeciwu w ciągu czternastu dni od
 * jego wniesienia jest równoznaczne z UWZGLĘDNIENIEM sprzeciwu.
 */
export const PENALTY_OBJECTION_DECISION_DAYS = 14

/** Art. 113 § 1: po roku nienagannej pracy karę uważa się za niebyłą. */
export const PENALTY_VOID_MONTHS = 12

/**
 * Granica łączna kar pieniężnych w miesiącu: 1/10 wynagrodzenia do wypłaty po
 * potrąceniach z art. 87 § 1 pkt 1–3, w punktach bazowych (art. 108 § 3 k.p.).
 */
export const PENALTY_MONTHLY_LIMIT_BP = 1000

/** Stan postępowania: od wymierzenia, przez sprzeciw, do zatarcia. */
export const PENALTY_STATUSES = [
  /** Kara wymierzona i zawiadomienie wydane (art. 110). */
  'zastosowana',
  /** Pracownik wniósł sprzeciw (art. 112 § 1) — czeka na decyzję. */
  'sprzeciw',
  /** Sprzeciw odrzucony — kara zostaje w mocy. */
  'sprzeciw_odrzucony',
  /** Sprzeciw uwzględniony — kara upada, a kwotę zwracamy (art. 112 § 3). */
  'sprzeciw_uwzgledniony',
  /** Kara uznana za niebyłą (art. 113) — odpis zawiadomienia znika z akt. */
  'niebyla',
] as const
export type PenaltyStatus = (typeof PENALTY_STATUSES)[number]

/**
 * Podstawa uznania kary za niebyłą (art. 113 k.p., fala 8, grupa 52):
 *  - `rok_nienagannej_pracy` — po roku nienagannej pracy (§ 1 zd. 1), dopiero
 *    od dnia zatarcia,
 *  - `inicjatywa_pracodawcy` i `wniosek_zwiazku` — wcześniejsze uznanie
 *    z inicjatywy pracodawcy albo na wniosek organizacji związkowej (§ 1 zd. 2),
 *  - `uwzglednienie_sprzeciwu` — tylko po uwzględnionym sprzeciwie (§ 2),
 *  - `orzeczenie_sadu_pracy` — sąd pracy uchylił karę po odrzuconym sprzeciwie
 *    (§ 2, droga z art. 112 § 2).
 * Dwie ostatnie zwracają karę pieniężną (art. 112 § 3).
 */
export const PENALTY_VOID_BASES = [
  'rok_nienagannej_pracy',
  'inicjatywa_pracodawcy',
  'wniosek_zwiazku',
  'uwzglednienie_sprzeciwu',
  'orzeczenie_sadu_pracy',
] as const
export type PenaltyVoidBasis = (typeof PENALTY_VOID_BASES)[number]

/** Czy uznanie za niebyłą z tej podstawy zwraca karę pieniężną (art. 112 § 3 k.p.). */
export function voidBasisRefunds(basis: PenaltyVoidBasis | null): boolean {
  return basis === 'uwzglednienie_sprzeciwu' || basis === 'orzeczenie_sadu_pracy'
}

/**
 * Czy podstawa pasuje do stanu sprawy — jedna reguła dla trasy i dla wyboru
 * w formularzu: rok nienagannej pracy dopiero od dnia zatarcia (art. 113 § 1
 * zd. 1), uwzględnienie sprzeciwu tylko po uwzględnionym sprzeciwie,
 * orzeczenie sądu pracy tylko po odrzuconym. Wcześniejsze uznanie z inicjatywy
 * pracodawcy albo na wniosek związku (§ 1 zd. 2) przysługuje zawsze.
 */
export function voidBasisIssue(
  basis: PenaltyVoidBasis,
  penalty: { status: string; voidOn: string },
  today: string,
): 'VOID_TOO_EARLY' | 'BASIS_MISMATCH' | null {
  if (basis === 'rok_nienagannej_pracy' && today < penalty.voidOn) return 'VOID_TOO_EARLY'
  if (basis === 'uwzglednienie_sprzeciwu' && penalty.status !== 'sprzeciw_uwzgledniony') {
    return 'BASIS_MISMATCH'
  }
  if (basis === 'orzeczenie_sadu_pracy' && penalty.status !== 'sprzeciw_odrzucony') {
    return 'BASIS_MISMATCH'
  }
  return null
}

/** Kto rozstrzygnął sprzeciw: kadry albo milczenie z art. 112 § 1 zd. 2 k.p. */
export const PENALTY_OBJECTION_DECIDERS = ['kadry', 'milczenie'] as const
export type PenaltyObjectionDecider = (typeof PENALTY_OBJECTION_DECIDERS)[number]

/** Czy kara jest w mocy — tylko taka wchodzi do potrącenia i do akt. */
export function isPenaltyEffective(status: PenaltyStatus): boolean {
  return status === 'zastosowana' || status === 'sprzeciw' || status === 'sprzeciw_odrzucony'
}

/**
 * Wierzyciel potrącenia z kary pieniężnej — stała systemowa, nie tekst
 * wpisany przez kadry (fala 9, grupa 94). Wpływy z kar idą na poprawę
 * warunków BHP (art. 108 § 4 k.p.), więc wierzycielem jest pracodawca;
 * ekran tłumaczy ją kluczem `garnishments.creditorPenalty`.
 */
export const PENALTY_GARNISHMENT_CREDITOR = 'Pracodawca — kara pieniężna (art. 108 k.p.)'
